import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, LogOut, Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API } from '@/lib/api';

export default function PendingApproval({ user }) {
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Abgemeldet');
      navigate('/login?logout=1', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login?logout=1', { replace: true });
    }
  };

  const handleRefresh = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (response.data.is_approved || response.data.role === 'admin') {
        toast.success('Freigabe aktiv. Weiterleitung...');
        navigate('/dashboard');
      } else {
        toast.info('Dein Zugang wartet noch auf Freigabe.');
      }
    } catch (error) {
      toast.error('Status konnte nicht aktualisiert werden');
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
      >
        <Card className="max-w-md w-full border-0 shadow-xl" data-testid="pending-approval-card">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
            <CardTitle className="heading-lg text-slate-900">Freigabe ausstehend</CardTitle>
            <CardDescription className="text-stone-500 mt-2">
              Dein Google-Konto wurde erkannt und wartet jetzt auf Freigabe
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-stone-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-white font-semibold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-medium text-slate-900">{user?.name || 'Benutzer'}</p>
                  <p className="text-sm text-stone-500">{user?.email || ''}</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-stone-600 text-center">
                Ein Admin muss deinen Zugriff einmal freischalten. Danach kannst du die Immobilienkarte ganz normal nutzen.
              </p>
              
              <div className="flex items-center justify-center gap-2 text-stone-500">
                <Mail className="w-4 h-4" />
                <span className="text-sm">Bei Bedarf kannst du den Admin direkt informieren</span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                data-testid="refresh-status-btn"
                onClick={handleRefresh}
                variant="outline"
                className="flex-1 h-11 rounded-full border-stone-300"
              >
                Status prüfen
              </Button>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="ghost"
                className="h-11 px-4 text-stone-600 hover:text-slate-900"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Abmelden
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
